'use client';

import React, { useState } from 'react';
import { ImageRotator, Card, Select, ImageWithSignedFallback, Heading } from '@ui';
import { withOptionalId } from '@lib/withOptionalId';
import { Section, Container } from '@layout';
import MarkupSnippet, { markupCopyLabels } from '../markup-snippet/MarkupSnippet';

const rotatorImagesEn = [
  { src: 'tomate-cherry-sungold-01', alt: 'Sungold Cherry Tomatoes' },
  { src: 'hot-pepper-carolina-reaper-02', alt: 'Carolina Reaper Hot Pepper' },
  {
    src: 'hot-pepper-jays-red-ghost-scorpion-03',
    alt: "Jay's Red Ghost Scorpion Hot Pepper",
  },
];

const rotatorImagesEs = [
  { src: 'tomate-cherry-sungold-01', alt: 'Tomates cherry Sungold' },
  { src: 'hot-pepper-carolina-reaper-02', alt: 'Pimiento Carolina Reaper' },
  {
    src: 'hot-pepper-jays-red-ghost-scorpion-03',
    alt: 'Pimiento picante Red Ghost Scorpion de Jay',
  },
];

// Available transitions supported by ImageRotator
type TransitionType =
  | 'fade'
  | 'slide'
  | 'crossfade'
  | 'zoom'
  | 'flip'
  | 'kenburns'
  | 'dissolve'
  | 'none';

const RotatorSelect: React.FC<{ isSpanish: boolean }> = ({ isSpanish }) => {
  const [transition, setTransition] = useState<TransitionType>('fade');
  const t = (en: string, es: string) => (isSpanish ? es : en);

  return (
    <Card className='rotator-select-card'>
      <div className='grid-2'>
        <label className='sub gap-16'>
          {t('Select a Transition:', 'Selecciona una transición:')}
          <Select
            className='form-select'
            value={transition}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setTransition(e.target.value as TransitionType)
            }
            aria-label={t(
              'Select image rotator transition',
              'Selecciona la transición del rotador de imágenes'
            )}
            options={[
              { value: 'fade', label: 'fade' },
              { value: 'slide', label: 'slide' },
              { value: 'crossfade', label: 'crossfade' },
              { value: 'zoom', label: 'zoom' },
              { value: 'flip', label: 'flip' },
              { value: 'kenburns', label: 'kenburns' },
              { value: 'dissolve', label: 'dissolve' },
              { value: 'none', label: 'none' },
            ]}
          />
        </label>
        <ImageRotator
          className='image-rotator'
          images={isSpanish ? rotatorImagesEs : rotatorImagesEn}
          interval={3000}
          width={'100%'}
          height={'auto'}
          transition={transition}
        />
      </div>
    </Card>
  );
};

interface ImageRotatorDemoProps {
  sectionID?: string;
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  isSpanish?: boolean;
}

const ImageRotatorDemo: React.FC<ImageRotatorDemoProps> = ({
  isSpanish = false,
  sectionID = '',
  classes = '',
  containerId,
  containerClasses,
}) => {
  const t = (en: string, es: string) => (isSpanish ? es : en);

  return (
    <Section {...withOptionalId(sectionID, 'meter')} className={classes}>
      <Container containerId={containerId} containerClasses={containerClasses}>
        <Heading as='h3'>{t('Image Rotator', 'Rotador de imágenes')}</Heading>
        <p className='sub full'>
          <strong>ImageRotator</strong>{' '}
          {isSpanish
            ? 'recorre una lista de imágenes en un intervalo configurable. Props clave:'
            : 'cycles through an array of images on a configurable interval. Key props:'}
        </p>
        <ul className='sub full'>
          <li>
            <code>images</code> — {t('array of', 'lista de')} {`{src, alt}`}
          </li>
          <li>
            <code>interval</code> —{' '}
            {t('rotation period in milliseconds', 'periodo de rotación en milisegundos')}
          </li>
          <li>
            <code>transition</code> —{' '}
            {t(
              'animation style (fade, slide, zoom, flip, kenburns, dissolve, crossfade, none)',
              'estilo de animacion (fade, slide, zoom, flip, kenburns, dissolve, crossfade, none)'
            )}
          </li>
          <li>
            <code>transitionDuration</code> —{' '}
            {t(
              'animation duration in ms (controls CSS and timing)',
              'duracion de la animacion en ms (controla CSS y el tiempo)'
            )}
          </li>
          <li>
            <code>width</code> / <code>height</code> —{' '}
            {t('sizing; accepts numbers or CSS strings', 'tamaño; acepta numeros o cadenas de CSS')}
          </li>
        </ul>
        <p className='sub full'>
          {isSpanish ? (
            <>
              El componente renderiza diapositivas por capas para animaciones de entrada y salida
              suaves y respeta <code>prefers-reduced-motion</code>. En desarrollo, las imagenes
              remotas quedan sin optimizar para evitar errores del optimizador de Next.js.
            </>
          ) : (
            <>
              The component renders layered slides for smooth enter/exit animations and honours{' '}
              <code>prefers-reduced-motion</code>. Remote images are left unoptimized in dev to
              avoid Next.js optimizer errors.
            </>
          )}
        </p>

        <RotatorSelect isSpanish={isSpanish} />

        {/* <Card className='grid-3'>
        <ImageRotator
          className='image-rotator'
          images={rotatorImages}
          interval={3000}
          width={'100%'}
          height={'auto'}
          transition='slide'
          renderImage={(img) => (
            <ImageWithSignedFallback
              src={img.src}
              alt={img.alt}
              style={{ width: '100%', height: 'auto' }}
            />
          )}
        />
        <ImageRotator
          className='image-rotator'
          images={rotatorImages}
          interval={3000}
          width={'100%'}
          height={'auto'}
          transition='zoom'
          transitionDuration={700}
        />
      </Card> */}
      <MarkupSnippet
          {...markupCopyLabels(isSpanish)}
          code={`<div class="image-rotator image-rotator--slide">
  <div class="slide is-active"><img src="/images/example.webp" alt="" /></div>
  <div class="slide"><img src="/images/example-2.webp" alt="" /></div>
</div>`}
        />
      </Container>
    </Section>
  );
};

export default ImageRotatorDemo;
